import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import { alertDialog } from '../utils/dialog';

// A plain, standalone support/contact form. On submit it calls the
// `submitSupportForm` Cloud Function, which sends the submission straight to the
// support inbox by email (via SendGrid). No ticket, no pipeline — just a simple
// form that sends an email when submitted.

const CATEGORIES = ['Question', 'Bug', 'Feature request', 'Billing', 'Other'];
const blankForm = () => ({ name: '', email: '', category: 'Question', subject: '', message: '' });

export default function SupportView() {
  const [form, setForm] = useState(blankForm());
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // holds the captured payload

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const payload = { ...form };
    try {
      const submitSupportForm = httpsCallable(functions, 'submitSupportForm');
      await submitSupportForm(payload);
      setSubmitted({ ...payload, submittedAt: new Date().toISOString() });
      setForm(blankForm());
    } catch (err) {
      await alertDialog({
        title: 'Could not send',
        message: err?.message || 'Something went wrong sending your request. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="support-view">
      <div className="support-head">
        <h1>🎫 Contact support</h1>
        <p>Send us a message below and it will be emailed straight to our support inbox. We'll get back to you at the email address you provide.</p>
      </div>

      {submitted ? (
        <div className="support-done">
          <div className="support-done-icon">✓</div>
          <h2>Thanks — your message was sent.</h2>
          <p>We've emailed your request to our support inbox and will reply to <strong>{submitted.email}</strong>.</p>
          <button className="btn-primary" onClick={() => setSubmitted(null)}>Submit another</button>
        </div>
      ) : (
        <form className="support-form" onSubmit={handleSubmit}>
          <div className="support-field">
            <label>Your name</label>
            <input type="text" placeholder="Jane Doe" value={form.name} onChange={set('name')} />
          </div>
          <div className="support-field">
            <label>Email</label>
            <input type="email" placeholder="jane@company.com" value={form.email} onChange={set('email')} required />
          </div>
          <div className="support-field">
            <label>Category</label>
            <select value={form.category} onChange={set('category')}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="support-field">
            <label>Subject</label>
            <input type="text" placeholder="Short summary" value={form.subject} onChange={set('subject')} required />
          </div>
          <div className="support-field">
            <label>Message</label>
            <textarea rows={5} placeholder="Tell us what you need…" value={form.message} onChange={set('message')} required />
          </div>
          <button type="submit" className="btn-primary support-submit" disabled={submitting}>
            {submitting ? 'Sending…' : 'Submit request'}
          </button>
        </form>
      )}
    </div>
  );
}
